'use client';

import { useInView } from '@/hooks/useInView';
import { CountUp } from '@/components/motion/CountUp';
import { cn } from '@/lib/utils';
import { FLAT_FEE, COMMISSION_LOW, commissionAt, savingsVsCommission } from '@/lib/savings';

const ROWS = [
  { price: 1_000_000, label: '$1M home' },
  { price: 1_500_000, label: '$1.5M home' },
  { price: 2_000_000, label: '$2M home' },
  { price: 2_500_000, label: '$2.5M home' },
];

const RATE_LABEL = `${(COMMISSION_LOW * 100).toFixed(1).replace(/\.0$/, '')}%`;

/**
 * The "rude math" visual: a conservative 2.5% commission bar vs. our flat-fee
 * sliver, bars grow on scroll, savings count up in gold. Figures are the honest
 * floor of a typical 2.5–3% commission — at 3% a buyer keeps even more.
 */
export function SavingsBars() {
  const { ref, inView } = useInView<HTMLDivElement>(0.3);
  const maxCommission = commissionAt(ROWS[ROWS.length - 1].price);

  return (
    <div ref={ref} className="space-y-8">
      {ROWS.map((row, i) => {
        const commission = commissionAt(row.price);
        const savings = savingsVsCommission(row.price);
        const commissionPct = (commission / maxCommission) * 100;
        const feePct = Math.max((FLAT_FEE / maxCommission) * 100, 1.25);

        return (
          <div key={row.price}>
            <div className="mb-2 flex items-baseline justify-between gap-4">
              <span className="text-sm font-semibold text-ink">{row.label}</span>
              <span className="text-sm text-ink-muted">
                you keep{' '}
                <CountUp
                  value={savings}
                  prefix="$"
                  className="font-display text-xl tabular-nums text-gold-400"
                />
              </span>
            </div>

            <div className="space-y-1.5">
              {/* Traditional commission bar */}
              <div className="flex items-center gap-3">
                <div className="h-3 flex-1 overflow-hidden rounded-full bg-glass">
                  <div
                    className={cn(
                      'h-full origin-left rounded-full bg-gradient-to-r from-coral-500/70 to-coral-400/90',
                      'transition-transform duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)]'
                    )}
                    style={{
                      width: `${commissionPct}%`,
                      transform: inView ? 'scaleX(1)' : 'scaleX(0)',
                      transitionDelay: `${i * 120}ms`,
                    }}
                  />
                </div>
                <span className="w-24 shrink-0 text-right text-xs tabular-nums text-coral-300">
                  {RATE_LABEL} = ${commission.toLocaleString()}
                </span>
              </div>

              {/* GoReal flat fee bar.
                  GOLD, NOT THE ACCENT, AND THIS IS A FIX RATHER THAN A
                  PREFERENCE. This bar was `bg-emerald-400` back when that token
                  was green, and the whole chart worked on one contrast: an
                  alarming coral commission against a reassuring green fee. The
                  crimson re-theme redefined `emerald-400` to #b12535 — a
                  near-neighbour of coral #b42318 — so both bars became the same
                  red and the only surviving signal was length. globals.css
                  predicted exactly this ("Coral carries price cuts and errors,
                  and crimson is its neighbour").
                  Gold is the right answer rather than merely a different one:
                  `text-gold-400` is already the colour of the savings figure on
                  this same row, so the bar and the number it produces now agree,
                  and gold reads as money kept where crimson reads as money
                  lost. */}
              <div className="flex items-center gap-3">
                <div className="h-3 flex-1 overflow-hidden rounded-full bg-glass">
                  <div
                    className={cn(
                      'h-full origin-left rounded-full bg-gold-500 shadow-glow-gold',
                      'transition-transform duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)]'
                    )}
                    style={{
                      width: `${feePct}%`,
                      transform: inView ? 'scaleX(1)' : 'scaleX(0)',
                      transitionDelay: `${i * 120 + 200}ms`,
                    }}
                  />
                </div>
                <span className="w-24 shrink-0 text-right text-xs tabular-nums text-gold-300">
                  GoReal ${FLAT_FEE.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
